import { Layout } from "antd";
import { Content } from "antd/es/layout/layout";
import _ from "lodash";
import React, { useState, useRef, useCallback } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import styled from "styled-components";

import { InnerContents } from "@components/InnerContents";
import { SideMenu } from "@components/SideMenu";
import { Text } from "@components/Text";
import { COLOR_BLACK, COLOR_GREY, COLOR_WHITE } from "@static/COLORS";
import { CONTENT_STYLES } from "@static/CONTENT_STYLES";
import { MENU_SIZE } from "@static/SIZE";

const SectionEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  height: auto;
`;

const SectionFrame = styled.div`
  position: relative;
  background: ${COLOR_WHITE};
  border: 1px solid ${COLOR_GREY};
  width: 100%;
  height: auto;
  aspect-ratio: ${({ width, height }) => `${width} / ${height}`};
  box-sizing: border-box;
`;

const ContainerMediaBlock = styled.div`
  position: absolute;
  top: ${({ y }) => y * 100}%;
  left: ${({ x }) => x * 100}%;
  background: ${({ backgroundColor }) => backgroundColor};
  width: ${({ width }) => width * 100}%;
  height: ${({ height }) => height * 100}%;
  aspect-ratio: ${({ width, height }) => `${width} / ${height}`};
  opacity: 0.5;
  cursor: move;
`;

const InterMediaBlock = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BLOCK_COLORS = [
  "Red",
  "Green",
  "Blue",
  "Yellow",
  "Purple",
  "Orange",
  "Pink",
  "Brown",
  "Cyan",
  "Gray",
];

const DEFAULT_BLOCK_LIST = [
  {
    key: "a958901d-cc53-45ec-8ed4-7dfb71ce518c",
    advertising_campaign_id: 6,
    name: "BLOCK-1",
    width: 200,
    height: 200,
    position_x: 0,
    position_y: 0,
  },
  {
    key: "ba8985e5-846d-47e0-87e4-8d8df23ef7c7",
    advertising_campaign_id: 6,
    name: "BLOCK-2",
    width: 200,
    height: 200,
    position_x: 0,
    position_y: 200,
  },
];

const DraggableBlock = ({
  id,
  x,
  y,
  width,
  height,
  backgroundColor,
  children,
  moveBlock,
}) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "block",
    item: { id, x, y, width, height },
    end: (item, monitor) => {
      console.log("Drag end function called");
      const delta = monitor.getDifferenceFromInitialOffset();
      console.log("Delta:", delta);
      const dropResult = monitor.getDropResult();
      console.log("Drop Result:", dropResult);
      if (dropResult && delta) {
        const { frameRect } = dropResult;
        const newX = (item.x * frameRect.width + delta.x) / frameRect.width;
        const newY = (item.y * frameRect.height + delta.y) / frameRect.height;
        console.log(`New X: ${newX}, New Y: ${newY}`);
        moveBlock(item.id, newX, newY);
      }
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  return (
    <ContainerMediaBlock
      ref={drag}
      x={x}
      y={y}
      width={width}
      height={height}
      backgroundColor={backgroundColor}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      {children}
    </ContainerMediaBlock>
  );
};

const DroppableFrame = ({ children, moveBlock, width, height }) => {
  const frameRef = useRef(null);

  const [, drop] = useDrop({
    accept: "block",
    drop: () => ({
      frameRect: frameRef.current
        ? frameRef.current.getBoundingClientRect()
        : null,
    }),
  });

  const combinedRef = useCallback(
    (element) => {
      frameRef.current = element;
      drop(element);
    },
    [drop]
  );

  return (
    <SectionFrame ref={combinedRef} width={width} height={height}>
      {children}
    </SectionFrame>
  );
};

export default () => {
  const [defaultBlocks, setDefaultBlocks] = useState(DEFAULT_BLOCK_LIST);

  const campaignWidth = 4800;
  const campaignHeight = 2400;

  const moveBlock = (id, newX, newY) => {
    console.log(
      `Move Block called - Block ID: ${id}, New X: ${newX}, New Y: ${newY}`
    );
    alert(
      `original=(x:${newX},y:${newY}) , convert=(x${newX * campaignWidth},y:${
        newY * campaignHeight
      })`
    );
    setDefaultBlocks((prevBlocks) =>
      prevBlocks.map((block) =>
        block.key === id
          ? {
              ...block,
              position_x: newX * campaignWidth,
              position_y: newY * campaignHeight,
            }
          : block
      )
    );
  };

  return (
    <Layout hasSider>
      <SideMenu />
      <Layout style={{ marginLeft: MENU_SIZE }}>
        <Content style={CONTENT_STYLES}>
          <InnerContents>
            <SectionEditor>
              <DndProvider backend={HTML5Backend}>
                <DroppableFrame
                  moveBlock={moveBlock}
                  width={campaignWidth}
                  height={campaignHeight}
                >
                  {_.map(defaultBlocks, (item, index) => {
                    const x = _.get(item, ["position_x"]) / campaignWidth;
                    const y = _.get(item, ["position_y"]) / campaignHeight;
                    const blockWidth = _.get(item, ["width"]);
                    const blockHeight = _.get(item, ["height"]);
                    const colorBlock = _.get(BLOCK_COLORS, [
                      index % _.size(BLOCK_COLORS),
                    ]);
                    return (
                      <DraggableBlock
                        key={item.key}
                        id={item.key}
                        x={x}
                        y={y}
                        width={blockWidth / campaignWidth}
                        height={blockHeight / campaignHeight}
                        backgroundColor={colorBlock}
                        moveBlock={moveBlock}
                      >
                        <InterMediaBlock>
                          <Text color={COLOR_BLACK} fontWeight="500">
                            #{index + 1}
                          </Text>
                        </InterMediaBlock>
                      </DraggableBlock>
                    );
                  })}
                </DroppableFrame>
              </DndProvider>
            </SectionEditor>
          </InnerContents>
        </Content>
      </Layout>
    </Layout>
  );
};
